(function () {
  // Maximum number of automatic clicks allowed
  const MAX_CLICKS = 8;
  let clickCount = 0;

  // Reference to the currently observed "Load More" button
  let loadMoreButton = null;

  // Debounce timeout duration in milliseconds (5 seconds)
  const DEBOUNCE_TIME = 1000;
  let debounceTimeout = null;

  /**
   * Checks if the given element is visible in the viewport.
   * @param {Element} el - The DOM element to check for visibility.
   * @returns {boolean} - Returns true if the element is visible, false otherwise.
   */
  function isElementVisible(el) {
    if (!el) return false;

    const rect = el.getBoundingClientRect();
    const style = window.getComputedStyle(el);

    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.top <
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.bottom > 0 &&
      rect.left < (window.innerWidth || document.documentElement.clientWidth) &&
      rect.right > 0 &&
      style.visibility !== 'hidden' &&
      style.display !== 'none' &&
      style.opacity !== '0'
    );
  }

  /**
   * Handles the automatic clicking of the "Load More" button with debounce.
   * @param {Element} button - The "Load More" button element to be clicked.
   */
  function handleButtonClick(button) {
    if (clickCount >= MAX_CLICKS) return;

    if (debounceTimeout) {
      // Debounce is active; skip this click
      return;
    }

    // Click the button and increment the click count
    button.click();
    clickCount++;
    console.log(`Load more button clicked ${clickCount} time(s).`);

    // Start the debounce timer
    debounceTimeout = setTimeout(() => {
      debounceTimeout = null;
      console.log('Debounce period ended. Ready for the next click.');
    }, DEBOUNCE_TIME);

    // If maximum clicks reached, disconnect observers and remove event listeners
    if (clickCount >= MAX_CLICKS) {
      disconnectObservers();
      console.log(
        'Maximum number of clicks reached. Observers and listeners removed.'
      );
    }
  }

  /**
   * Scroll event handler that checks the visibility of the "Load More" button.
   */
  function onScroll() {
    if (loadMoreButton && isElementVisible(loadMoreButton)) {
      handleButtonClick(loadMoreButton);
    }
  }

  /**
   * Sets up an IntersectionObserver to monitor the visibility of the "Load More" button.
   * @param {Element} button - The "Load More" button element to observe.
   */
  function observeButtonWithIntersectionObserver(button) {
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleButtonClick(entry.target);
          }
        });
      },
      {
        root: null, // Use the viewport as the root
        threshold: 0.1, // Trigger when 10% of the button is visible
      }
    );

    intersectionObserver.observe(button);
  }

  /**
   * Handles the setup when a "Load More" button is found in the DOM.
   * @param {Element} button - The "Load More" button element.
   */
  function handleLoadMoreButton(button) {
    loadMoreButton = button;

    if (isElementVisible(button)) {
      // If the button is already visible, click it immediately
      handleButtonClick(button);
    } else {
      // Otherwise, set up observers to monitor its visibility
      observeButtonWithIntersectionObserver(button);
      window.addEventListener('scroll', onScroll);
    }
  }

  /**
   * Searches for the "Load More" button within a given node and its descendants.
   * @param {Node} node - The DOM node to search within.
   */
  function searchForButton(node) {
    if (!(node instanceof Element)) return;

    // Check if the node itself is the "Load More" button
    if (node.matches('.rcc-search__loadmore__button')) {
      handleLoadMoreButton(node);
    }

    // Check for any "Load More" buttons within the node's descendants
    const buttons = node.querySelectorAll('.rcc-search__loadmore__button');
    buttons.forEach((button) => {
      handleLoadMoreButton(button);
    });
  }

  /**
   * Disconnects the MutationObserver and removes the scroll event listener.
   */
  function disconnectObservers() {
    if (mutationObserver) mutationObserver.disconnect();
    window.removeEventListener('scroll', onScroll);
  }

  /**
   * Initializes the MutationObserver to monitor the DOM for additions or class changes.
   */
  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      // Handle newly added nodes
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach((node) => {
          searchForButton(node);
        });
      }

      // Handle class attribute changes
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'class'
      ) {
        const target = mutation.target;
        if (
          target instanceof Element &&
          target.matches('.rcc-search__loadmore__button')
        ) {
          handleLoadMoreButton(target);
        }
      }
    });
  });

  // Start observing the document body for child additions and class attribute changes
  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class'], // Only observe changes to the 'class' attribute
  });

  // Initial check in case the "Load More" button is already present and visible
  const existingButtons = document.querySelectorAll(
    '.rcc-search__loadmore__button'
  );
  if (existingButtons.length > 0) {
    existingButtons.forEach((button) => {
      handleLoadMoreButton(button);
    });
  }
})();